/**
 * @file DialogChangePassword Component
 * @description This component provides a dialog interface for changing user passwords.
 * It includes form validation, password strength indicators, and API integration.
 */

import DialogWrapper from './dialog-wrapper';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import {
  Check,
  Circle,
  Close,
  LockOutlined,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { getColor } from '../../assets/theme';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import InputFieldsController from '../form/controller/input-fields.controller';
import { yupResolver } from '@hookform/resolvers/yup';
import { dialogChangePasswordSchema } from '../../utils/schema';
import { useEffect, useState } from 'react';
import useUser from '../../hooks/useUser';
import useDisclosure from '../../hooks/useDisclosure';
import { LoadingButton } from '@mui/lab';
import { useVerifyCredsMutation } from '../../api/users-management/user-mangement.api.rtk';
import { SHA512 } from 'crypto-js';
import useAuth from '../../hooks/useAuth';
import useAppContext from '@hooks/useAppContext';

/**
 * @interface DialogChangePasswordProps
 * @description Props for the DialogChangePassword component
 */
interface DialogChangePasswordProps {
  open: boolean;
  onClose: () => void;
}

/**
 * @interface FormValues
 * @description Structure for the form values
 */
interface FormValues {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

/**
 * @interface AlertTypes
 * @description Structure for alert messages
 */
interface AlertTypes {
  title: string;
  desc: string;
  color: string;
}

/**
 * DialogChangePassword Component
 * @param {DialogChangePasswordProps} props - Component props
 * @returns {JSX.Element} Rendered component
 */
const DialogChangePassword = ({ open, onClose }: DialogChangePasswordProps) => {
  const { primary, white, textSecondary, danger, success } = getColor();

  const { startLoading, stopLoading } = useAppContext();

  const userData = useUser();

  const { changePasswordV2, sendEmail } = useAuth();

  const [currentPass, setCurrentPass] = useState('');
  const [currentPassFocus, setCurrentPassFocus] = useState(false);
  const [isTempPass, setIsTempPass] = useState(false);

  const { isOpen: showCurrentPass, onToggle: onToggleCurrentPass } =
    useDisclosure();

  const { isOpen: showNewPass, onToggle: onToggleNewPass } = useDisclosure();

  const { isOpen: showConfrimPass, onToggle: onToggleConfrimPass } =
    useDisclosure();

  const [alertDetails, setAlertDetails] = useState<AlertTypes>({
    title: '',
    desc: '',
    color: '',
  });

  const {
    isOpen: showAlert,
    onOpen: openAlert,
    onClose: closeAlert,
  } = useDisclosure();

  const loadBtn = useDisclosure();

  const requestTemporaryPass = async () => {
    startLoading();
    await sendEmail('password').finally(stopLoading);
    setIsTempPass(true);
    reset();
  };

  const [verifyCreds, { isLoading, isError, reset: resetVerify }] =
    useVerifyCredsMutation();

  useEffect(() => {
    if (!open) {
      reset();
      resetVerify();
      clearErrors();
      closeAlert();
    }
  }, [open]);

  const methods = useForm<FormValues>({
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    resolver: yupResolver(dialogChangePasswordSchema),
    mode: 'onChange',
  });

  const {
    control,
    formState: { errors },
    handleSubmit,
    setError,
    watch,
    reset,
    clearErrors,
  } = methods;

  /**
   * Handle form submission
   * @param {FormValues} data - Form data
   */
  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    try {
      loadBtn.onOpen();
      const res: any = await changePasswordV2({
        route: 'password',
        data: {
          password: data.currentPassword,
          verifyPassword: data.confirmPassword,
          newPassword: data.newPassword,
        },
      });

      if (typeof res == 'string' || res?.errMsg) {
        setAlertDetails({
          title: 'Error',
          desc: res?.errMsg || res,
          color: danger,
        });
        loadBtn.onClose();
        return openAlert();
      }
      setAlertDetails({
        title: 'Success',
        desc: 'Password updated successfully',
        color: success,
      });
      reset();
      openAlert();
      loadBtn.onClose();
    } catch (error) {
      loadBtn.onClose();
    }
  };

  useEffect(() => {
    if (currentPassFocus || isTempPass) return;
    let debounce: any;

    if (debounce) {
      clearTimeout(debounce);
    }

    debounce = setTimeout(async () => {
      if (currentPass) {
        const res: any = await verifyCreds({
          userID: userData?.userID,
          password: currentPass,
          signature: SHA512(`${userData?.userID}${currentPass}`).toString(),
        });

        if (res?.error) {
          return setError('currentPassword', {
            type: 'manual',
            message: 'Wrong Password',
          });
        } else {
          clearErrors('currentPassword');
        }
      }
    }, 1000);

    return () => clearTimeout(debounce);
  }, [currentPass, currentPassFocus, isTempPass]);

  return (
    <DialogWrapper
      open={open}
      onClose={() => {
        onClose();
        setIsTempPass(false);
      }}
    >
      <Box sx={{ width: { xs: '100%', sm: 'unset' } }}>
        {showAlert && (
          <Fade in={true} timeout={{ enter: 500, exit: 300 }}>
            <Alert
              variant='filled'
              sx={{
                bgcolor: `${alertDetails.color}!important`,
                width: { xs: '100%', sm: '348px', lg: '740px' },
                mb: '12px',
                color: '#FFF',
              }}
              action={
                <IconButton
                  aria-label='close'
                  color='inherit'
                  size='small'
                  onClick={closeAlert}
                  disableRipple
                  sx={{
                    bgcolor: 'transparent',
                    border: 0,
                    ':hover': {
                      bgcolor: 'transparent',
                    },
                  }}
                >
                  <Close fontSize='inherit' />
                </IconButton>
              }
            >
              <AlertTitle>{alertDetails.title}</AlertTitle>
              <div
                dangerouslySetInnerHTML={{ __html: alertDetails.desc || '' }}
              />
            </Alert>
          </Fade>
        )}
        <Stack
          sx={{
            bgcolor: white,
            borderRadius: '4px',
            p: '24px',
            justifyContent: 'flex-start',
          }}
        >
          <Box sx={{ width: '100%' }}>
            <LockOutlined
              sx={{
                width: '59px',
                height: '59px',
                bgcolor: '#F7F7FB',
                p: '12px',
                borderRadius: '100%',
                color: primary,
              }}
            />
          </Box>
          <DialogTitle sx={{ p: '8px 0', fontWeight: 600 }}>
            Change Password
          </DialogTitle>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <DialogContent sx={{ p: '16px 0!important' }}>
                <Stack direction={{ xs: 'column', lg: 'row' }} gap='24px'>
                  <Box sx={{ width: '300px' }}>
                    <Typography sx={{ color: textSecondary }}>
                      Passwords must contain:
                    </Typography>
                    <List sx={{ p: 0 }}>
                      <ListItem sx={{ gap: '8px', p: 0 }}>
                        <ListItemIcon sx={{ minWidth: 'auto' }}>
                          {watch('newPassword')?.length >= 8 ? (
                            <Check
                              sx={{
                                color: success,
                                width: '18px',
                                height: '18px',
                              }}
                            />
                          ) : errors?.newPassword &&
                            watch('newPassword')?.length > 0 &&
                            watch('newPassword')?.length < 8 ? (
                            <Close
                              sx={{
                                color: danger,
                                width: '16px',
                                height: '16px',
                              }}
                            />
                          ) : (
                            <Circle
                              sx={{
                                color: primary,
                                width: '16px',
                                height: '16px',
                              }}
                            />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary='At least 8 characters'
                          sx={{
                            color:
                              errors?.newPassword &&
                              watch('newPassword')?.length > 0 &&
                              watch('newPassword')?.length < 8
                                ? danger
                                : textSecondary,
                            textDecoration:
                              watch('newPassword')?.length >= 8
                                ? 'line-through'
                                : 'none',
                          }}
                        />
                      </ListItem>

                      <ListItem sx={{ gap: '8px', p: 0 }}>
                        <ListItemIcon sx={{ minWidth: 'auto' }}>
                          {/[a-z]/.test(watch('newPassword')) ? (
                            <Check
                              sx={{
                                color: success,
                                width: '18px',
                                height: '18px',
                              }}
                            />
                          ) : errors?.newPassword &&
                            watch('newPassword')?.length > 0 &&
                            !/[a-z]/.test(watch('newPassword')) ? (
                            <Close
                              sx={{
                                color: danger,
                                width: '16px',
                                height: '16px',
                              }}
                            />
                          ) : (
                            <Circle
                              sx={{
                                color: primary,
                                width: '16px',
                                height: '16px',
                              }}
                            />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary='At least 1 lower case letter (a..z)'
                          sx={{
                            color:
                              errors?.newPassword &&
                              watch('newPassword')?.length > 0 &&
                              !/[a-z]/.test(watch('newPassword'))
                                ? danger
                                : textSecondary,
                            textDecoration: /[a-z]/.test(watch('newPassword'))
                              ? 'line-through'
                              : 'none',
                          }}
                        />
                      </ListItem>

                      <ListItem sx={{ gap: '8px', p: 0 }}>
                        <ListItemIcon sx={{ minWidth: 'auto' }}>
                          {/[A-Z]/.test(watch('newPassword')) ? (
                            <Check
                              sx={{
                                color: success,
                                width: '18px',
                                height: '18px',
                              }}
                            />
                          ) : errors?.newPassword &&
                            watch('newPassword')?.length > 0 &&
                            !/[A-Z]/.test(watch('newPassword')) ? (
                            <Close
                              sx={{
                                color: danger,
                                width: '16px',
                                height: '16px',
                              }}
                            />
                          ) : (
                            <Circle
                              sx={{
                                color: primary,
                                width: '16px',
                                height: '16px',
                              }}
                            />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary='At least 1 upper case letter (A..Z)'
                          sx={{
                            color:
                              errors?.newPassword &&
                              watch('newPassword')?.length > 0 &&
                              !/[A-Z]/.test(watch('newPassword'))
                                ? danger
                                : textSecondary,
                            textDecoration: /[A-Z]/.test(watch('newPassword'))
                              ? 'line-through'
                              : 'none',
                          }}
                        />
                      </ListItem>

                      <ListItem sx={{ gap: '8px', p: 0 }}>
                        <ListItemIcon sx={{ minWidth: 'auto' }}>
                          {watch('newPassword') &&
                          watch('currentPassword') &&
                          watch('newPassword') !== watch('currentPassword') ? (
                            <Check
                              sx={{
                                color: success,
                                width: '18px',
                                height: '18px',
                              }}
                            />
                          ) : watch('newPassword') &&
                            watch('currentPassword') &&
                            watch('newPassword') ===
                              watch('currentPassword') ? (
                            <Close
                              sx={{
                                color: danger,
                                width: '16px',
                                height: '16px',
                              }}
                            />
                          ) : (
                            <Circle
                              sx={{
                                color: primary,
                                width: '16px',
                                height: '16px',
                              }}
                            />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary='Cannot be the same as the current password'
                          sx={{
                            color:
                              watch('newPassword') &&
                              watch('currentPassword') &&
                              watch('newPassword') === watch('currentPassword')
                                ? danger
                                : textSecondary,
                            textDecoration:
                              watch('newPassword') &&
                              watch('currentPassword') &&
                              watch('newPassword') !== watch('currentPassword')
                                ? 'line-through'
                                : 'none',
                          }}
                        />
                      </ListItem>
                    </List>
                  </Box>
                  <Box sx={{ width: { xs: '100%', sm: '300px', lg: '368px' } }}>
                    <InputFieldsController
                      loading={isLoading}
                      label='Current Password'
                      name='currentPassword'
                      type={showCurrentPass ? 'text' : 'password'}
                      control={control}
                      errors={errors}
                      handleOnChange={setCurrentPass}
                      InputProps={{
                        endAdornment: watch('currentPassword') && (
                          <InputAdornment position='end'>
                            <IconButton
                              onClick={onToggleCurrentPass}
                              sx={{ border: 0 }}
                            >
                              {showCurrentPass ? (
                                <Visibility />
                              ) : (
                                <VisibilityOff />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      onFocus={() => setCurrentPassFocus(true)}
                      onBlur={() => setCurrentPassFocus(false)}
                      wrapperStyle={{
                        marginBottom: 1,
                      }}
                    />
                    <Button
                      sx={{
                        fontSize: '12px',
                        p: 0,
                        pb: 2.5,
                        ':hover': { bgcolor: 'transparent' },
                      }}
                      onClick={async () => await requestTemporaryPass()}
                    >
                      Request Temporary Password
                    </Button>
                    <InputFieldsController
                      label='New Password'
                      name='newPassword'
                      type={showNewPass ? 'text' : 'password'}
                      control={control}
                      errors={errors}
                      noTextError={watch('newPassword')?.length !== 0}
                      InputProps={{
                        endAdornment: watch('newPassword') && (
                          <InputAdornment position='end'>
                            <IconButton
                              onClick={onToggleNewPass}
                              sx={{ border: 0 }}
                            >
                              {showNewPass ? <Visibility /> : <VisibilityOff />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <InputFieldsController
                      label='Confirm Password'
                      name='confirmPassword'
                      type={showConfrimPass ? 'text' : 'password'}
                      control={control}
                      errors={errors}
                      InputProps={{
                        endAdornment: watch('confirmPassword') && (
                          <InputAdornment position='end'>
                            <IconButton
                              onClick={onToggleConfrimPass}
                              sx={{ border: 0 }}
                            >
                              {showConfrimPass ? (
                                <Visibility />
                              ) : (
                                <VisibilityOff />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                </Stack>
              </DialogContent>
              <DialogActions sx={{ p: '0 0 8px 0' }}>
                <Stack direction='row' gap='8px'>
                  <Button variant='outlined' onClick={onClose}>
                    Cancel
                  </Button>
                  <LoadingButton
                    variant='contained'
                    type='submit'
                    disabled={isError}
                    loading={loadBtn.isOpen}
                  >
                    Update
                  </LoadingButton>
                </Stack>
              </DialogActions>
            </form>
          </FormProvider>
        </Stack>
      </Box>
    </DialogWrapper>
  );
};

export default DialogChangePassword;
